import { useTerminology } from '@/lib/useTerminology'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formCard, pageTitle, sectionTitle, pageWide } from '@/lib/form-styles'
import { FeldStatus, FeldStatusZeichen, useFeld, type Feld, type Speicherstelle } from '@/lib/speichern'
import { useZeitKonfiguration } from '@/lib/zeit-konfiguration'
import type { DataSourceMode } from '../types/time-tracking.types'
import {
  Check,
  Pencil,
  Plug,
  Type,
  ArrowRight,
  Key,
  ScrollText,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react'

/**
 * „Woher kommen die Daten?" — definiert NUR die Quelle der Stammdaten je Bereich
 * (Projekte / Tätigkeiten / Mitarbeiter): selbst pflegen / aus anderem Programm / Freitext.
 *
 * Saubere Trennung im Einrichtungs-Menü:
 *   Woher kommen die Daten = Quelle · Eigene Felder = Felddefinition ·
 *   Auswahllisten = Listeninhalte · Konfigurationen = Buchungs-Einstellungen.
 *
 * Pro Bereich + Modus passende Verknüpfung: selbst → Datenmaske · extern → API-Schlüssel
 * + Sync-Protokoll · Freitext → nichts. Jede Wahl wird sofort gespeichert (AP-36).
 *
 * Zugriff: ADMIN+ (ProtectedRoute in App.tsx).
 */

interface ModeDef {
  value: DataSourceMode
  label: string
  hint: string
  icon: LucideIcon
}

const MODES: ModeDef[] = [
  { value: 'INTERNAL', label: 'Ich pflege selbst', hint: 'In Symphenia anlegen und aktuell halten.', icon: Pencil },
  { value: 'EXTERNAL_API', label: 'Aus meinem anderen Programm', hint: 'Per Synchronisation übernommen; die Maske ist dann nur lesbar.', icon: Plug },
  { value: 'MANUAL', label: 'Freitext', hint: 'Freie Eingabe ohne Liste.', icon: Type },
]

export default function DataPathPage() {
  const { t } = useTranslation()
  const { stelle, geladen, fehler } = useZeitKonfiguration()
  if (fehler) {
    return (
      <div className={pageWide}>
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>
      </div>
    )
  }
  if (!geladen) return <div className="p-6 text-sm text-muted-foreground">{t('common.loading')}</div>
  return <Datenbezug stelle={stelle as Speicherstelle<unknown>} />
}

/**
 * AP-36 Phase C — jede Kachel speichert sofort (Auswahl, 3.2). Früher sammelte
 * die Seite 600 ms und schickte alle drei Quellen zusammen (S-2).
 */
function Datenbezug({ stelle }: { stelle: Speicherstelle<unknown> }) {
  // AP-16: Begriff des Mandanten (Projekt / Auftrag)
  const terms = useTerminology()
  const { t } = useTranslation()
  const [showTech, setShowTech] = useState(false)
  const alsQuelle = { art: 'auswahl' as const, lies: (x: string) => x as DataSourceMode, schreib: (w: DataSourceMode) => w ?? 'INTERNAL' }
  const projekte = useFeld<DataSourceMode>(stelle, 'projectSource', { ...alsQuelle, label: terms.projectPlural })
  const taetigkeiten = useFeld<DataSourceMode>(stelle, 'activityTypeSource', { ...alsQuelle, label: 'Tätigkeiten' })
  const mitarbeiter = useFeld<DataSourceMode>(stelle, 'userSource', { ...alsQuelle, label: 'Mitarbeiter' })
  const projectSource = projekte.wert

  return (
    <div className={pageWide}>
      <div>
        <h1 className={pageTitle}>{t('dataPath.title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Lege je Bereich fest, woher die Stammdaten kommen. Gepflegt werden sie auf den verlinkten Masken.
          Jede Wahl wird sofort gespeichert.
        </p>
      </div>

      <SourceCard
        title={terms.projectPlural}
        description={`${terms.projectPlural} inkl. ${terms.positionPlural} und Arbeitsschritte — woher kommen sie?`}
        feld={projekte}
        dataMaskHref="/time-tracking/projects"
        allowManual
        managedNote={
          projectSource === 'PROJECT_MGMT' ? (
            // AP-35 (Detailblatt 4 6.1): angeschlossen wird in den Einstellungen des
            // Projektmanagements, darum gibt es hier keine Kachel dafür. Eine andere
            // Kachel zu wählen, trennt die Zeiterfassung wieder ab.
            <div className="space-y-2">
              <p className="text-sm text-foreground">
                Angeschlossen — Quelle: {terms.management}. {terms.projectPlural}, {terms.positionPlural} und
                Arbeitsschritte werden dort gepflegt und hierher übernommen.
              </p>
              <InlineLink to="/projects/list" icon={ArrowRight} label={`${terms.projectPlural} öffnen`} />
            </div>
          ) : null
        }
      />
      <SourceCard
        title="Tätigkeiten"
        description="Die Auswahlliste der Tätigkeitsarten — woher kommt sie?"
        feld={taetigkeiten}
        dataMaskHref="/time-tracking/activity-types"
      />
      <SourceCard
        title="Mitarbeiter"
        description="Die Mitarbeiter-Stammdaten — woher kommen sie?"
        feld={mitarbeiter}
        dataMaskHref="/time-tracking/users"
      />

      {/* Technische Details (ausklappbar) */}
      <div>
        <button
          type="button"
          onClick={() => setShowTech((v) => !v)}
          className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${showTech ? '' : '-rotate-90'}`} />
          Technische Details zur ERP-Anbindung
        </button>
        {showTech && (
          <div className="mt-2 space-y-1.5 rounded-md border border-border bg-muted/20 p-3 text-xs leading-relaxed text-muted-foreground">
            <p>
              Bei „aus deinem anderen Programm" sendet dein anderes Programm die Stammdaten über die Sync-API — in der
              Reihenfolge {terms.projectPlural} → Positionen → Schritte (Tätigkeiten und Mitarbeiter jederzeit). Die Zuordnung
              erfolgt über <code className="font-mono">externalId</code> + <code className="font-mono">source</code>;
              vorhandene Einträge werden aktualisiert, neue angelegt.
            </p>
            <p>Solange ein Bereich auf „aus anderem Programm" steht, ist die manuelle Bearbeitung dort gesperrt (nur lesen). Die Zugänge richtest du unter „API-Schlüssel" ein.</p>
          </div>
        )}
      </div>
    </div>
  )
}

/** Eine Bereich-Karte (gleiches Layout wie die übrigen Cockpit-Karten) */
function SourceCard({
  title,
  description,
  feld,
  dataMaskHref,
  allowManual = false,
  managedNote = null,
}: {
  title: string
  description: string
  feld: Feld<DataSourceMode>
  dataMaskHref: string
  allowManual?: boolean
  managedNote?: ReactNode
}) {
  const modes = allowManual ? MODES : MODES.filter((m) => m.value !== 'MANUAL')
  const value = feld.wert ?? 'INTERNAL'
  return (
    <section className={`${formCard} space-y-4`}>
      <div>
        <h2 className={`${sectionTitle} flex items-center gap-2`}>
          {title} <FeldStatusZeichen status={feld} />
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        <FeldStatus status={feld} zeichen={false} />
      </div>
      <div className={`grid gap-3 ${allowManual ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        {modes.map((m) => (
          <ChoiceTile
            key={m.value}
            icon={m.icon}
            selected={value === m.value}
            onClick={() => feld.setze(m.value)}
            title={m.label}
            hint={m.hint}
          />
        ))}
      </div>
      {value === 'INTERNAL' && (
        <Reveal>
          <InlineLink to={dataMaskHref} icon={Pencil} label={`${title} pflegen`} />
        </Reveal>
      )}
      {managedNote && <Reveal>{managedNote}</Reveal>}
      {value === 'EXTERNAL_API' && (
        <Reveal>
          <div className="space-y-2">
            <InlineLink to="/integration/api-keys" icon={Key} label="API-Schlüssel einrichten" />
            <InlineLink to="/integration/sync-log" icon={ScrollText} label="Sync-Protokoll ansehen" />
          </div>
        </Reveal>
      )}
    </section>
  )
}

function Reveal({ children }: { children: ReactNode }) {
  return <div className="rounded-md border border-border bg-background p-4">{children}</div>
}

function InlineLink({ to, icon: Icon, label }: { to: string; icon: LucideIcon; label: string }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 text-sm transition-colors hover:border-primary/50 hover:bg-muted/40"
    >
      <Icon className="h-5 w-5 flex-shrink-0 text-primary" />
      <span className="flex-1 font-medium text-foreground">{label}</span>
      <ArrowRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
    </Link>
  )
}

function ChoiceTile({
  icon: Icon,
  selected,
  onClick,
  title,
  hint,
}: {
  icon: LucideIcon
  selected: boolean
  onClick: () => void
  title: string
  hint: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors ${
        selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 flex-shrink-0 text-primary" />
        <span className="font-medium">{title}</span>
        {selected && <Check className="ml-auto h-4 w-4 flex-shrink-0 text-primary" />}
      </div>
      <span className="text-sm text-muted-foreground">{hint}</span>
    </button>
  )
}
